/**
 * AIDEV-NOTE [MEETBALL magic-link]: the ONE cross-tab mirror mechanism.
 *
 * sessionStorage is per-tab, and a magic link is opened in a NEW tab, frequently
 * in another browser or on another device. By the time the callback lands, the
 * sessionStorage stash written before the email was sent is simply not there, so
 * every auth-gated CTA silently degraded to a plain /home landing. Both the
 * redirect path (@utils/auth) and the auth intent (@utils/authIntent) are
 * therefore MIRRORED into localStorage, but only when the /auth door flow is
 * actually entered (see the `routeToDoor` branches in useSignInOut.ts and
 * useAuthNavigation.ts) -- never on an ordinary sign-in, which has no cross-tab
 * hop to survive.
 *
 * This module exists because that mechanism used to live TWICE, once per
 * consumer, with its own key, TTL constant, envelope type, reader, writer and
 * clear. The copies drifted (one read `globalThis.localStorage`, the other bare
 * `localStorage`; both writers early-returned on an absent value instead of
 * clearing a stale mirror), and the same defect had to be fixed twice. Keep new
 * mirrors here: `storageCleanup.ts` spreads `MAGIC_LINK_MIRROR_KEYS` into its
 * localStorage sweep, so anything added to this object is cleaned on sign-out by
 * construction.
 *
 * Deliberately a LEAF module: it imports `./logger`, `./authFlowTtl` and
 * `./sessionSlot`, and none of those import anything, so every consumer can
 * depend on this with no cycle.
 */
import { AUTH_FLOW_TTL_MS } from './authFlowTtl';
import { decodeEnvelope, encodeEnvelope, type Storable } from './sessionSlot';
import { logger } from './logger';

/**
 * Every magic-link localStorage mirror key. Spread into
 * `storageCleanup.ts`'s LOCAL_STORAGE_KEYS, so a mirror never outlives a
 * sign-out: these carry ONE user's destination and pending action, and a
 * survivor would execute against whoever signs in next on the same browser
 * profile.
 */
export const MAGIC_LINK_MIRROR_KEYS = {
  redirectPath: 'mb.magicLink.redirectPath',
  intent: 'mb.magicLink.intent',
  /**
   * The guest pending-question claim ENTRIES -- a `PendingQuestionClaimEntry[]` read from
   * `sessionStorage['intended_action_claims']`, not the legacy `intended_action` flag.
   *
   * Third because it has the same shape of problem as the other two and none of
   * the visibility: `hasPendingQuestionClaim()` reads sessionStorage, both claim
   * triggers (useCallbackRedirect for an existing user, submitHelpers via
   * SignupFlow for a new one) call it, and the magic-link tab's sessionStorage is
   * empty by construction. Without the mirror both checks return false, the
   * guest's question is never claimed to their new account, and nothing surfaces
   * anywhere. On the flag-off path sign-in was a same-tab full-page redirect, so
   * sessionStorage survived and the claim ran.
   *
   * A LIST AND NOT A BOOLEAN, since the claim names the rows it may publish: the only writer is
   * `mirrorPendingQuestionClaimForMagicLink`, which writes nothing at all for a tab holding only
   * the flag (a row nothing can name must not travel profile-wide), and withholds the write
   * entirely for a tab whose entries were DELIVERED to it. The only reader validates with
   * `isPendingQuestionClaimEntries` and arms entries, never the flag. Both live in
   * `src/utils/pendingQuestionClaim.ts`.
   *
   * AIDEV-NOTE [MEETBALL magic-link continuation]: A CONTINUATION SIGN-IN LEAVES THIS STANDING,
   * and that is accepted. The door finishes a continuation by redeeming the approved flow's token
   * itself (`useClaimContinuation` in `AuthDoor.tsx`), so that sign-in never passes through
   * `/auth/link` in the profile that asked, and `/auth/link`'s hydrate is the one reader that
   * consumes this mirror. `/callback`'s settle (`settlePendingQuestionClaim`) clears it on a
   * success or a permanent failure. A retryable failure keeps it, as that tab keeps its own
   * entries for the retry, and an abandoned handoff never reaches `/callback` at all; either way
   * it stands until its TTL, the next door visit (which rewrites it from that tab's claim or
   * clears it), or sign-out. It falls within the loss Dexter accepted when he cancelled TECH-534,
   * and it is bounded where it is read: the only later consumer is an `/auth/link` gate, which
   * names the claim and never auto-proceeds on one, and the backend publishes a claimed row only
   * when it was filed under the signing-in address.
   *
   * It is deliberately NOT cleared on the continuation path. This is an `intent` mirror whose
   * owners are already the door, `/auth/link`'s hydrate, `/callback`'s settle and its error branch
   * (`clearMagicLinkMirrors`), the TTL and sign-out. A clear in `AuthDoor`'s claim or abandon path
   * would be a second owner in a module that does not own this key, and it would fire before the
   * redemption is known to succeed: the "two clearers for one marker" shape that
   * `clearMagicLinkMirrors` below argues against.
   */
  pendingQuestionClaim: 'mb.magicLink.pendingQuestionClaim',
  /**
   * The address the FIRST magic link out of this flow was sent to, which is what
   * binds the capabilities in the three mirrors above to a recipient -- the intent's
   * invitation token, the destination's secret params, and the claim entries whose
   * secrets the flow read resolves to question titles (TECH-540).
   *
   * AIDEV-NOTE [MEETBALL magic-link]: it has to be a MIRROR, not just the per-tab
   * key it started as, and the reason is that the thing it guards already travels.
   * `mb.magicLink.intent` carries a space invitation token and
   * `mb.magicLink.redirectPath` can carry `?invite=`, and `/auth/link` hydrates both
   * into a tab that never sent anything -- so every route from there back to the
   * door (the spent-link CTA, the unnamed-link CTA, the failure reveal's fresh-link
   * link, `/callback`'s "Sign in again", and logto-ui's expired/consumed bounce)
   * produced a door holding A's invitation with no binding at all. A send to B's
   * address from that tab read `readFirstSendAddress()` as null, concluded "first
   * recipient", and minted A's token into B's inbox in full. During the rollout
   * window -- backend flag off, every flow read 404ing -- that is not an edge case,
   * it is the path.
   *
   * The binding travels with what it binds, on the same envelope, with the same TTL
   * and the same clear-on-absent rule, which is also why it cannot end up outliving
   * the capability: the door's mount writes all four together, so a tab arriving
   * with nothing pending empties the binding at the same moment it empties the
   * intent.
   */
  firstSendAddress: 'mb.magicLink.firstSendAddress',
  /**
   * The pending continuation records this device is polling on — up to five
   * `{flowId, requestSecret, createdAt}` pairs handed back by the 202 on
   * `POST /api/v1/auth/magic-link`. Read and written only through
   * `@lib/magicLink/continuation`, which owns the pruning, the cap and the
   * serialisation; nothing else touches this key.
   *
   * AIDEV-NOTE [MEETBALL magic-link continuation]: the fifth mirror, and the
   * first one that is a CAPABILITY the device holds rather than a note about a
   * flow. `requestSecret` is what the status poll and the claim authenticate
   * with, so it never enters a URL, an href, a log, a Sentry breadcrumb or an
   * analytics payload — analytics carry `flowHash(flowId)` instead.
   *
   * It is a mirror rather than a per-tab stash for the plain reason that the
   * door and the approval are, by construction, not the same tab: the visitor
   * asks here and approves from their mail client, frequently on another
   * device. A reload of the door, or a second tab on the same profile, has to
   * find the record it is waiting on.
   *
   * Being in `MAGIC_LINK_MIRROR_KEYS` puts it in `storageCleanup.ts`'s
   * `LOCAL_STORAGE_KEYS` spread, which sweeps it on sign-out — right, because a
   * live continuation is an unattended way into an account and has no business
   * outliving the session that asked for it.
   *
   * It is the one mirror `AUTH_HANDOVER_KEYS` in that same file explicitly
   * EXCLUDES, so `/auth/link`'s account-switch gates sweep it rather than
   * preserving it (id 4015057883). It used to be preserved, on the argument
   * that the same-device happy path needs the record to survive; the argument
   * does not hold, because the record is an address-less bearer capability and
   * `AuthDoor` adopts whatever this browser profile holds. The full reasoning
   * and what the sweep costs are in the note on that set; if you change one,
   * change both.
   *
   * It is the one key `clearMagicLinkMirrors()` skips, and for a reason of its
   * own: see the note on that function. It was enrolled there by construction
   * when this key was added, which put a live capability at the mercy of
   * `/callback`'s error branch.
   *
   * On native the same key holds the same JSON in Capacitor Preferences (no
   * envelope: the records carry their own `createdAt` and the module prunes on
   * every read). See the adapter note in `continuation.ts`.
   */
  continuation: 'mb.magicLink.continuation',
} as const;

export type MagicLinkMirrorKey =
  (typeof MAGIC_LINK_MIRROR_KEYS)[keyof typeof MAGIC_LINK_MIRROR_KEYS];

/**
 * What a mirror IS, which is what every sweep over this family actually wants
 * to know.
 *
 * AIDEV-NOTE [MEETBALL magic-link continuation]: id 4074238784, written about
 * the world this same commit replaced, so read it in the past tense. Three
 * sweeps generalised over `Object.values(MAGIC_LINK_MIRROR_KEYS)` and then
 * discriminated by NAMING the keys they meant to treat differently --
 * `storageCleanup`'s sign-out list took all six, its `AUTH_HANDOVER_KEYS`
 * filtered `continuation` out by name, and `clearMagicLinkMirrors` skipped
 * `signedInCue` and `continuation` by name. TWO of those three now select by
 * KIND (`AUTH_HANDOVER_KEYS` in `storageCleanup.ts`, and `clearMagicLinkMirrors`
 * below); the sign-out list there, `LOCAL_STORAGE_KEYS`, still spreads every
 * mirror, deliberately, because "everything" is what it means. So a new key's
 * behaviour was decided by its absence from two exclusion lists, and
 * `storageCleanup` stated that default aloud: "a future seventh mirror is
 * preserved by construction, as before". For an intent note that default is
 * right. For a bearer capability it is wrong in both directions at once --
 * preserved across an account switch, and destroyed by an unrelated
 * `/callback` error.
 *
 * `Record<keyof typeof MAGIC_LINK_MIRROR_KEYS, ...>` makes the classification
 * EXHAUSTIVE: a key added above without a kind here is a `tsc` error at
 * this object, on the line where the question can still be answered, rather
 * than a silent default three files away.
 *
 *  - `intent`   a note about ONE sign-in: where the visitor was going, what
 *               they had asked for. Voiding these is what a failed redemption
 *               owes the next person on the profile.
 *  - `capability` a live bearer credential. Belongs to the SEND rather than to
 *               any one redemption, so it outlives a failed one and must not
 *               outlive a change of account.
 *
 * (There was a third kind, `cue`, for the "Signed in as" marker. #1117 moved
 * that marker to per-tab sessionStorage, so it is no longer a mirror at all.)
 */
type MagicLinkMirrorKind = 'intent' | 'capability';

export const MAGIC_LINK_MIRROR_KINDS: Record<
  keyof typeof MAGIC_LINK_MIRROR_KEYS,
  MagicLinkMirrorKind
> = {
  redirectPath: 'intent',
  intent: 'intent',
  // 'intent', although TECH-540 calls a claim secret "a capability", as the intent's
  // invitation token already was. Two senses of one word: `strip*Capabilities` means
  // "must not travel to a second recipient"; this KIND means a bearer credential that
  // signs somebody in and belongs to the SEND, which is `continuation` alone. A claim
  // belongs to the redemption in flight (see AUTH_HANDOVER_KEYS in storageCleanup.ts),
  // publishes only rows filed under the signing-in address, and is voided by a failed
  // redemption. Do not reclassify it by the word.
  pendingQuestionClaim: 'intent',
  firstSendAddress: 'intent',
  continuation: 'capability',
};

/** Every mirror key of the given kinds. The one way the sweeps should select. */
export const magicLinkMirrorKeysOfKind = (
  ...kinds: readonly MagicLinkMirrorKind[]
): MagicLinkMirrorKey[] =>
  Object.entries(MAGIC_LINK_MIRROR_KEYS)
    .filter(([name]) =>
      kinds.includes(MAGIC_LINK_MIRROR_KINDS[name as keyof typeof MAGIC_LINK_MIRROR_KEYS]),
    )
    .map(([, key]) => key);

/**
 * Writes (or, on `null`, REMOVES) a mirror.
 *
 * The null branch is the whole reason this takes a nullable value. Both writers
 * used to early-return when there was nothing to mirror, which left the previous
 * flow's envelope in place: a guest who tapped Join, entered the door and
 * abandoned it would have that intent executed by an unrelated sign-in half an
 * hour later, because the next `/auth/link` opened in this profile HYDRATES
 * whatever mirror it finds into its own tab's sessionStorage. No reader falls
 * through to a mirror -- `readAuthIntent()` and friends are sessionStorage-only
 * (see that function) -- so a mirror has exactly one consumer, and that consumer
 * cannot tell whose flow wrote it. Entering the door is the point where the
 * mirror must be made to describe THIS flow, including when this flow has
 * nothing to say.
 *
 * Returns whether the write (or the removal) landed. A write the storage refuses
 * leaves the previous envelope where it was; the three payload mirrors write
 * through `writeMagicLinkPayloadMirror`, which removes it instead.
 */
export const writeMagicLinkMirror = (key: MagicLinkMirrorKey, value: unknown): boolean => {
  try {
    if (value === null) {
      globalThis.localStorage.removeItem(key);
      return true;
    }
    // The callers hold `unknown` (a sanitiser's result, a test's seed); `null` was
    // handled above and `JSON.stringify` drops an `undefined` value either way.
    globalThis.localStorage.setItem(key, encodeEnvelope(value as Storable, Date.now()));
    return true;
  } catch (e) {
    logger.warn(`[magicLinkMirror] Failed to write ${key}:`, e);
    return false;
  }
};

/**
 * Reads a mirror, honouring the TTL and the caller's own payload validation.
 *
 * A mirror can carry sensitive data (the redirect path may embed an invite
 * query param; the intent payload may name a community). So a rejected
 * envelope -- not JSON, bad shape, expired, or failing `isValid` -- is removed here
 * rather than merely reported as absent: `null` on its own would leave an
 * abandoned flow's payload sitting in localStorage for the rest of its
 * 30-minute TTL with nothing left to re-check it. Missing (`!raw`) is not a
 * rejection and removes nothing; a throwing storage answers `null` too (see
 * `readMagicLinkMirrorOrUnreadable` for the caller that must tell the two apart).
 */
export const readMagicLinkMirror = <T extends Storable>(
  key: MagicLinkMirrorKey,
  isValid: (value: unknown) => value is T,
): T | null => readMagicLinkMirrorOrUnreadable(key, isValid) ?? null;

/**
 * `readMagicLinkMirror`, but a read that THREW answers `undefined` instead of `null`, for the
 * caller that has to tell "storage would not say" from "nothing there" (the same shape as
 * `readAuthIntentOrUnreadable`). ONE `getItem` answers both questions: a second read can
 * disagree with the first. Only a throw from `getItem` is `undefined`; everything it returned
 * is decided by the decode, so a missing, empty, expired, non-JSON or wrong-shape value is
 * `null`, and a rejected one is removed. That removal has its OWN guard: a storage that
 * refuses it still answers `null`, because the read itself succeeded.
 */
export const readMagicLinkMirrorOrUnreadable = <T extends Storable>(
  key: MagicLinkMirrorKey,
  isValid: (value: unknown) => value is T,
): T | null | undefined => {
  let raw: string | null;
  try {
    raw = globalThis.localStorage.getItem(key);
  } catch (e) {
    logger.warn(`[magicLinkMirror] Failed to read ${key}:`, e);
    return undefined;
  }
  if (!raw) {
    return null;
  }

  const value = decodeEnvelope(raw, AUTH_FLOW_TTL_MS, isValid, Date.now());
  if (value === null) {
    try {
      globalThis.localStorage.removeItem(key);
    } catch (e) {
      logger.warn(`[magicLinkMirror] Failed to remove rejected ${key}:`, e);
    }
  }
  return value;
};

/** Drops a mirror. Same as writing `null`, named for the intent at the call site. */
export const clearMagicLinkMirror = (key: MagicLinkMirrorKey): void => {
  writeMagicLinkMirror(key, null);
};

/** The three mirrors that carry a flow's PAYLOAD: where it goes, what it asked for, what it claims. */
type MagicLinkPayloadMirrorKey =
  | typeof MAGIC_LINK_MIRROR_KEYS.redirectPath
  | typeof MAGIC_LINK_MIRROR_KEYS.intent
  | typeof MAGIC_LINK_MIRROR_KEYS.pendingQuestionClaim;

/**
 * Writes one of the payload mirrors, and FAILS CLOSED: a write the storage refuses
 * removes the key rather than leave the envelope an earlier write left. Returns
 * whether the new value landed.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: the envelope an earlier write left is another
 * flow's, or a less stripped copy of this one. The case that made this a rule: a
 * tab bound to a@b.co sends to stranger@c.co, the door writes the STRIPPED intent and
 * destination for stranger's link, and storage refuses both writes, so a@b.co's
 * whole invite stayed on the envelope for any link redeemed in this profile. The
 * door's own fail-closed strip clears only the tab (`holdStashToRule` in
 * `@lib/magicLink/capabilityStash`), so the writer has to. `removeItem` needs no
 * quota, which is why it can still land when `setItem` cannot. The cost is the fast
 * path for this link, which a refused write had already lost.
 *
 * Deliberately NOT for the other two keys. Removing the BINDING
 * (`firstSendAddress`) would open the tab it is hydrated into to re-addressing: a
 * hydrated tab with no binding reads any address as the first recipient, so a stale
 * binding is the safer survivor. `continuation` holds live bearer records that
 * `@lib/magicLink/continuation` owns, and a refused rewrite must not destroy the
 * links still out. The key type admits only the three payload keys.
 */
export const writeMagicLinkPayloadMirror = (
  key: MagicLinkPayloadMirrorKey,
  value: unknown,
): boolean => {
  if (writeMagicLinkMirror(key, value)) {
    return true;
  }
  clearMagicLinkMirror(key);
  return false;
};

/**
 * Drops the profile-wide envelope: every mirror EXCEPT `continuation`.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: for the caller that has to destroy what this
 * BROWSER PROFILE can see while keeping what THIS TAB knows -- the `/callback`
 * error branch (see Callback.tsx). sessionStorage is scoped to the ONE TAB that
 * wrote it -- on native, read that as the WebView process, which can outlive
 * what a visitor thinks of as closing the app -- so a stash left there can
 * only ever be read in that tab, by that visitor. A mirror is not scoped to
 * anything: it is hydrated by the next `/auth/link` opened anywhere in the
 * profile, which may well be a link somebody else requested on another
 * device. A redemption that failed has no claim on that next visitor, so the
 * envelope goes even though the tab's own copies stay for the retry.
 *
 * AIDEV-NOTE [MEETBALL magic-link continuation]: `continuation` is excluded
 * because it was enrolled here by accident (Group H of
 * PR #1143 round 1, magicLinkMirror.ts:137). Every other key in this
 * object is a note about ONE auth intent -- where the visitor was going, what
 * they had asked for -- and voiding them is exactly what a failed redemption
 * owes the next person in this profile. The continuation records are not that:
 * they are a live CAPABILITY, one or more links that are still out and still
 * the visitor's to approve, and they belong to the send rather than to the
 * redemption. Sweeping them on a `/callback` error destroyed a pending link
 * that had nothing to do with the one that had just failed -- a visitor who
 * requested a second link, then opened the dead first one, lost the door's
 * ability to wait on the good one. They already have four owners that ARE
 * about their lifecycle: the TTL, `forgetContinuation` per terminal flow,
 * `clearContinuations` on sign-out, and `clearOutgoingAccountStorage` on an
 * account switch. This function is not a fifth. The key's
 * own docstring above reasons about the two `storageCleanup.ts` spreads and
 * says BOTH are intended; this third sweep was never among them.
 */
export const clearMagicLinkMirrors = (): void => {
  // Selected by KIND rather than by skipping a name (id 4074238784): this
  // function clears the notes about the sign-in that just failed, and a mirror
  // that is not such a note has no business being decided here by omission.
  for (const key of magicLinkMirrorKeysOfKind('intent')) {
    clearMagicLinkMirror(key);
  }
};
